import { AuthApiError, getCurrentUser, refreshTokenApi } from './auth-service';
import { type AuthData, type User, saveAuthData } from './auth-storage';

/**
 * 서버에 저장된 최신 사용자 정보로 맞춘 결과.
 * - unchanged: 바뀐 것이 없거나 확인하지 못했다. 저장된 값을 그대로 쓴다.
 * - updated: 저장소를 갱신했다. 화면 상태도 이 값으로 바꾼다.
 * - signedOut: 로그아웃해야 한다(역할이 비었거나 토큰을 되살리지 못했다).
 */
export type AuthUserSyncResult =
  | { kind: 'unchanged' }
  | { kind: 'updated'; authData: AuthData }
  | { kind: 'signedOut' };

// 앱 시작과 창 포커스가 겹쳐도 /me 요청은 한 번만 보낸다.
let inflight: Promise<AuthUserSyncResult> | null = null;

/**
 * 로그인할 때 받은 사용자 정보를 서버의 최신 값으로 맞춘다.
 * 운영에서 DB로 역할을 바꿔도, 저장된 값만 쓰면 재로그인 전까지 화면에 반영되지 않는다.
 *
 * 서버 API 권한은 토큰 안의 역할로 판단하므로, 역할이 바뀌었으면 토큰도 새로 받는다.
 * (재발급 시 서버가 DB에서 역할을 다시 읽는다.) 그러지 않으면 화면은 바뀐 역할로
 * 그려져도 그 역할의 API가 토큰이 만료될 때까지 403이 난다.
 *
 * @param tokensJustIssued 방금 재발급한 토큰이면 이미 최신 역할이 담겨 있어 다시 받지 않는다.
 */
export function syncAuthUserWithServer(
  stored: AuthData,
  { tokensJustIssued = false }: { tokensJustIssued?: boolean } = {},
): Promise<AuthUserSyncResult> {
  if (inflight) return inflight;
  inflight = runSync(stored, tokensJustIssued).finally(() => {
    inflight = null;
  });
  return inflight;
}

async function runSync(
  stored: AuthData,
  tokensIssuedBeforeSync: boolean,
): Promise<AuthUserSyncResult> {
  let current = stored;
  let tokensJustIssued = tokensIssuedBeforeSync;
  let me: unknown;
  try {
    me = await getCurrentUser();
  } catch (error) {
    if (!isUnauthorized(error)) return { kind: 'unchanged' };
    // 저장된 토큰을 서버가 거부했다. 다른 API의 401처럼 재발급 후 한 번 더 확인한다.
    try {
      current = { ...stored, tokens: await refreshTokenApi() };
      saveAuthData(current);
      tokensJustIssued = true;
    } catch {
      return { kind: 'signedOut' };
    }
    try {
      me = await getCurrentUser();
    } catch (retryError) {
      // 방금 받은 토큰도 거부되면 세션을 살릴 수 없다(역할을 모두 잃었거나 유저가 없다).
      if (isUnauthorized(retryError)) return { kind: 'signedOut' };
      return current === stored
        ? { kind: 'unchanged' }
        : { kind: 'updated', authData: current };
    }
  }

  // 응답 구조가 예상과 다르면(역할 배열이 없으면) 확인하지 못한 것으로 보고 저장된 값을
  // 그대로 쓴다. 빈 값으로 읽으면 역할이 비어 로그아웃 규칙을 타 버린다.
  // 역할 배열이 명시적으로 비어 있을 때만 아래에서 로그아웃한다.
  if (!Array.isArray((me as Partial<User> | null)?.roles)) {
    return current === stored
      ? { kind: 'unchanged' }
      : { kind: 'updated', authData: current };
  }

  const user = toUser(me as Partial<User>);
  if (!user.roles.length) return { kind: 'signedOut' };

  const rolesChanged = !hasSameRoles(user.roles, current.user.roles);
  if (!rolesChanged && isSameUser(user, current.user)) {
    return current === stored
      ? { kind: 'unchanged' }
      : { kind: 'updated', authData: current };
  }

  let tokens = current.tokens;
  if (rolesChanged && !tokensJustIssued) {
    try {
      tokens = await refreshTokenApi();
    } catch (error) {
      // refreshTokenApi는 실패하면 저장소를 비운다. 재발급 거부(401)가 아니면
      // 저장된 값을 되살려 그대로 쓰고, 다음 확인 때 다시 맞춘다.
      if (isUnauthorized(error)) return { kind: 'signedOut' };
      saveAuthData(current);
      return current === stored
        ? { kind: 'unchanged' }
        : { kind: 'updated', authData: current };
    }
  }

  const authData: AuthData = { user, tokens };
  saveAuthData(authData);
  return { kind: 'updated', authData };
}

function isUnauthorized(error: unknown): boolean {
  return error instanceof AuthApiError && error.status === 401;
}

/** 로그인 응답과 같은 모양으로 맞춘다. 저장된 값과 비교하려면 필드 구성이 같아야 한다. */
function toUser(me: Partial<User>): User {
  return {
    id: me.id ?? '',
    email: me.email ?? '',
    name: me.name ?? '',
    lang: me.lang ?? '',
    roles: me.roles ?? [],
    companyId: me.companyId ?? '',
    isRecordingConsented: me.isRecordingConsented ?? null,
    passwordChange: me.passwordChange,
  };
}

function hasSameRoles(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sorted = [...b].sort();
  return [...a].sort().every((role, i) => role === sorted[i]);
}

function isSameUser(a: User, b: User): boolean {
  // 역할은 순서만 다를 수 있어 정렬해 비교한다.
  const normalize = (u: User) =>
    JSON.stringify({ ...toUser(u), roles: [...(u.roles ?? [])].sort() });
  return normalize(a) === normalize(b);
}
